import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Play } from "lucide-react";

const rnd = (n) => Math.floor(Math.random() * n);
const pick = (a) => a[rnd(a.length)];
const shuffle = (a) => [...a].sort(() => Math.random() - 0.5);

// Build a 4-option set with one correct numeric/string answer + 3 distractors.
function opts(correct, makeWrong) {
  const set = new Set([String(correct)]);
  let guard = 0;
  while (set.size < 4 && guard++ < 50) set.add(String(makeWrong()));
  return shuffle([...set]).map((label) => ({ label, correct: label === String(correct) }));
}

const COUNTRIES = [
  ["France", "Paris"], ["Japan", "Tokyo"], ["Egypt", "Cairo"], ["Canada", "Ottawa"],
  ["Brazil", "Brasília"], ["Italy", "Rome"], ["Spain", "Madrid"], ["Kenya", "Nairobi"],
  ["Norway", "Oslo"], ["Peru", "Lima"], ["Greece", "Athens"], ["Cuba", "Havana"],
];
const SYN = [["happy", "joyful"], ["fast", "quick"], ["big", "huge"], ["smart", "clever"], ["angry", "furious"], ["tired", "weary"], ["calm", "serene"], ["brave", "bold"]];
const SPELL = [["necessary", "neccessary", "necesary"], ["definitely", "definately", "definitly"], ["separate", "seperate", "seperete"], ["receive", "recieve", "receeve"], ["rhythm", "rythm", "rhythem"], ["beautiful", "beutiful", "beautifull"]];
const ROMAN = { 4: "IV", 9: "IX", 14: "XIV", 19: "XIX", 40: "XL", 50: "L", 90: "XC", 6: "VI", 11: "XI", 24: "XXIV" };

const CONFIG = {
  "math-tf": { title: "True or False Math", hint: "Is the equation correct?", binary: ["True", "False"],
    gen: () => { const a = rnd(12) + 1, b = rnd(12) + 1, op = pick(["+", "-", "×"]); let r = op === "+" ? a + b : op === "-" ? a - b : a * b; const shown = Math.random() < 0.5 ? r : r + pick([-2, -1, 1, 2, 3]); return { prompt: `${a} ${op} ${b} = ${shown}`, answer: shown === r ? "True" : "False" }; } },
  "quick-add": { title: "Quick Addition", hint: "Add the numbers fast!",
    gen: () => { const a = rnd(40) + 1, b = rnd(40) + 1; return { prompt: `${a} + ${b}`, options: opts(a + b, () => a + b + pick([-5, -2, -1, 1, 2, 3, 5])) }; } },
  "quick-mult": { title: "Times Table Blitz", hint: "Multiply quickly!",
    gen: () => { const a = rnd(11) + 2, b = rnd(11) + 2; return { prompt: `${a} × ${b}`, options: opts(a * b, () => a * b + pick([-6, -4, -2, 2, 4, 6, a, -a])) }; } },
  "bigger-number": { title: "Bigger Number", hint: "Tap the larger number.", twoCol: true,
    gen: () => { let a = rnd(9999), b = rnd(9999); if (a === b) b += 1; return { prompt: "Which is bigger?", options: shuffle([{ label: String(a), correct: a > b }, { label: String(b), correct: b > a }]) }; } },
  "odd-even": { title: "Odd or Even", hint: "Is the number odd or even?", binary: ["Odd", "Even"],
    gen: () => { const n = rnd(999) + 1; return { prompt: String(n), answer: n % 2 ? "Odd" : "Even" }; } },
  "which-prime": { title: "Spot the Prime", hint: "Tap the prime number.",
    gen: () => { const isP = (x) => { if (x < 2) return false; for (let i = 2; i * i <= x; i++) if (x % i === 0) return false; return true; }; const primes = [2, 3, 5, 7, 11, 13, 17, 19, 23, 29, 31, 37]; const p = pick(primes); const set = new Set([String(p)]); while (set.size < 4) { const c = rnd(40) + 2; if (!isP(c)) set.add(String(c)); } return { prompt: "Which is prime?", options: shuffle([...set]).map((l) => ({ label: l, correct: l === String(p) })) }; } },
  "roman-numerals": { title: "Roman Numerals", hint: "What number is this?",
    gen: () => { const keys = Object.keys(ROMAN); const k = pick(keys); return { prompt: ROMAN[k], options: opts(k, () => Number(pick(keys))) }; } },
  "emoji-count": { title: "Count the Emojis", hint: "How many do you see?",
    gen: () => { const n = rnd(8) + 3, e = pick(["👾", "⭐", "🍕", "🔥", "🎮", "👻"]); return { prompt: e.repeat(n), big: true, options: opts(n, () => n + pick([-2, -1, 1, 2, 3])) }; } },
  "spelling-bee": { title: "Spelling Bee", hint: "Pick the correct spelling.",
    gen: () => { const w = pick(SPELL); return { prompt: "Spelled correctly?", options: shuffle(w).map((l, i) => ({ label: l, correct: l === w[0] })) }; } },
  "capital-quiz": { title: "Capital Cities", hint: "Pick the capital city.",
    gen: () => { const [c, cap] = pick(COUNTRIES); return { prompt: `Capital of ${c}?`, options: opts(cap, () => pick(COUNTRIES)[1]) }; } },
  "synonym-match": { title: "Synonym Match", hint: "Pick the synonym.",
    gen: () => { const [w, s] = pick(SYN); return { prompt: `Synonym of "${w}"`, options: opts(s, () => pick(SYN)[1]) }; } },
  "bigger-fraction": { title: "Bigger Fraction", hint: "Which fraction is larger?", twoCol: true,
    gen: () => { const f = () => { const d = rnd(8) + 2; return [rnd(d) + 1, d]; }; let [n1, d1] = f(), [n2, d2] = f(); while (n1 / d1 === n2 / d2) { [n2, d2] = f(); } return { prompt: "Larger fraction?", options: shuffle([{ label: `${n1}/${d1}`, correct: n1 / d1 > n2 / d2 }, { label: `${n2}/${d2}`, correct: n2 / d2 > n1 / d1 }]) }; } },
  "count-vowels": { title: "Count the Vowels", hint: "How many vowels in the word?",
    gen: () => { const w = pick(["ARCADE", "JOYSTICK", "AVATAR", "PIXEL", "NEON", "QUIZ", "PUZZLE", "VICTORY", "LEGEND"]); const v = (w.match(/[AEIOU]/g) || []).length; return { prompt: w, options: opts(v, () => v + pick([-1, 1, 2, -2])) }; } },
  "order-ops": { title: "Order of Operations", hint: "Solve the expression.",
    gen: () => { const a = rnd(6) + 1, b = rnd(6) + 1, c = rnd(6) + 1; const r = a + b * c; return { prompt: `${a} + ${b} × ${c}`, options: opts(r, () => r + pick([-5, -3, -1, 2, 4, (a + b) * c - r])) }; } },
  "color-id": { title: "Name That Color", hint: "Tap the color you SEE.",
    gen: () => { const C = [["Red", "#FF003C"], ["Cyan", "#00F0FF"], ["Lime", "#39FF14"], ["Yellow", "#EFFF04"], ["Purple", "#a855f7"]]; const [name, hex] = pick(C); return { prompt: "●", promptColor: hex, big: true, options: shuffle(C).slice(0, 4).concat([[name, hex]]).filter((v, i, arr) => arr.findIndex((x) => x[0] === v[0]) === i).slice(0, 4).map(([n]) => ({ label: n, correct: n === name })) }; } },
  "double-it": { title: "Double Trouble", hint: "Double the number!",
    gen: () => { const n = rnd(50) + 1; return { prompt: `2 × ${n}`, options: opts(n * 2, () => n * 2 + pick([-4, -2, 2, 4, n])) }; } },
};

export function SpeedChoice({ variant, onScore }) {
  const cfg = CONFIG[variant];
  const DURATION = 45;
  const [round, setRound] = useState(null);
  const [score, setScore] = useState(0);
  const [time, setTime] = useState(DURATION);
  const [running, setRunning] = useState(false);
  const [over, setOver] = useState(false);
  const [flash, setFlash] = useState(null);
  const reported = useRef(false);

  useEffect(() => {
    if (!running) return;
    if (time <= 0) { setRunning(false); setOver(true); return; }
    const t = setTimeout(() => setTime((x) => x - 1), 1000);
    return () => clearTimeout(t);
  }, [running, time]);

  const start = () => { setScore(0); setTime(DURATION); setRunning(true); setOver(false); setRound(cfg.gen()); reported.current = false; };

  const answer = (val) => {
    if (!running) return;
    const correct = cfg.binary ? val === round.answer : val.correct;
    if (correct) { setScore((s) => s + 1); setFlash("ok"); } else { setScore((s) => Math.max(0, s - 1)); setFlash("no"); }
    setTimeout(() => setFlash(null), 150);
    setRound(cfg.gen());
  };

  useEffect(() => { if (over && !reported.current) { reported.current = true; onScore?.(score * 40); } }, [over, score, onScore]);

  const options = cfg.binary ? cfg.binary.map((b) => ({ label: b })) : (round?.options || []);

  return (
    <div className="flex flex-col items-center w-full max-w-md" data-testid="speed-choice">
      <div className="flex items-center justify-between w-full mb-5 font-mono">
        <span className="text-neon-cyan text-lg" data-testid="sc-score">SCORE: {score}</span>
        <span className={`text-lg ${time <= 10 ? "text-neon-pink animate-blink" : "text-neon-yellow"}`}>⏱ {time}s</span>
      </div>

      {!running ? (
        <div className="text-center py-8">
          {over && <p className="font-display font-black text-2xl text-neon-lime uppercase mb-2" data-testid="sc-result">Time! Score {score}</p>}
          <p className="text-zinc-400 mb-4 max-w-xs mx-auto">{cfg.hint}</p>
          <Button onClick={start} data-testid="sc-start" className="bg-neon-cyan text-black rounded-none font-bold uppercase h-12 px-8">
            <Play className="w-4 h-4 mr-2 fill-black" /> {over ? "Play Again" : "Start"}
          </Button>
        </div>
      ) : (
        <>
          <div className={`w-full text-center py-10 rounded-xl border-2 mb-6 bg-zinc-900 transition-colors ${flash === "ok" ? "border-neon-lime" : flash === "no" ? "border-neon-pink" : "border-zinc-700"}`}>
            <span className="font-display font-black break-words" style={{ fontSize: round?.big ? "2.5rem" : "2.25rem", color: round?.promptColor || "#fff" }}>{round?.prompt}</span>
          </div>
          <div className={`grid ${options.length === 2 ? "grid-cols-2" : "grid-cols-2"} gap-3 w-full`}>
            {options.map((o, i) => (
              <button key={i} onClick={() => answer(cfg.binary ? o.label : o)} data-testid={`sc-option-${i}`}
                className="py-5 rounded-lg bg-zinc-950 border-2 border-zinc-700 hover:border-neon-cyan font-display font-bold text-xl transition-colors break-words px-2">
                {o.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export const SPEED_CHOICE_IDS = Object.keys(CONFIG);
export const speedChoiceTitle = (id) => CONFIG[id]?.title;
